// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import DirectSelect from '@mapbox/mapbox-gl-draw/src/modes/direct_select';
import * as cfm from '@mapbox/mapbox-gl-draw/src/lib/constrain_feature_movement';
import moveFeatures from '@mapbox/mapbox-gl-draw/src/lib/move_features';
import { curveToCoords, regularizeMidpoints } from 'WktCurves';
import doubleClickZoom from '@mapbox/mapbox-gl-draw/src/lib/double_click_zoom';
import mouseEventPoint from '@mapbox/mapbox-gl-draw/src/lib/mouse_event_point';
import customCreateSupplementaryPoints from './customCreateSupplementaryPoints';
import moveCurve from './moveCurve';

const Constants = require('@mapbox/mapbox-gl-draw/src/constants');
const CommonSelectors = require('@mapbox/mapbox-gl-draw/src/lib/common_selectors');

const {
  noTarget,
  isInactiveFeature,
} = require('@mapbox/mapbox-gl-draw/src/lib/common_selectors');

const constrainFeatureMovement = cfm.default;

const CustomDirectSelect = { ...DirectSelect };

function isCircle([type, arc, next] = []) {
  if (!type) {
    return false;
  }
  if (type === 'curvepolygon') {
    return arc && isCircle(arc) && !next;
  }

  return (
    type === 'compoundcurve' &&
    arc &&
    !next &&
    arc[0] === 'circularstring' &&
    arc.length === 4 &&
    arc[3][0] === arc[1][0] &&
    arc[3][1] === arc[1][1]
  );
}

// CustomDirectSelect.__onSetup = DirectSelect.onSetup;

CustomDirectSelect.onSetup = function (opts) {
  const { featureId } = opts;
  const { useMode } = opts;
  const feature = this.getFeature(featureId);

  if (!feature) {
    throw new Error('You must provide a featureId to enter direct_select mode');
  }

  if (feature.type === Constants.geojsonTypes.POINT) {
    throw new TypeError('direct_select mode doesn\'t handle point features');
  }

  // Introduce a new option in the direct select state to differentiate between polygons drawn in the editor tool
  // and polygons drawn for kml boundaries to allow creating midpoints for kml polygons
  const state = {
    featureId,
    feature,
    useMode,
    dragMoveLocation: opts.startPos || null,
    dragMoving: false,
    canDragMove: false,
    selectedCoordPaths: opts.coordPath ? [opts.coordPath] : [],
  };

  this.setSelectedCoordinates(this.pathsToCoordinates(featureId, state.selectedCoordPaths));
  this.setSelected(featureId);
  doubleClickZoom.disable(this);

  this.setActionableState({
    trash: true,
  });

  if (state.feature.properties.curve && state.feature.type === 'Polygon') {
    state.feature.setCoordinates([curveToCoords(state.feature.properties.curve)]);
  }
  if (state.feature.properties.curve) {
    state.feature.setCoordinates(curveToCoords(state.feature.properties.curve));
  }
  return state;
};

CustomDirectSelect.onClick = function (state, e) {
  // eslint-disable-line consistent-return
  if (noTarget(e)) {
    if (state.useMode === 'kmlDraw') {
      this.clickActiveFeature(state, e);
    } else {
      this.clickNoTarget(state, e);
    }
    return;
  }
  if (CommonSelectors.isActiveFeature(e)) {
    this.clickActiveFeature(state, e);
    return;
  }
  if (isInactiveFeature(e)) {
    this.clickInactive(state, e);
    return;
  }

  this.stopDragging(state);
  // Enter draw_poly_line mode when clicked on the first or the last vertex of the lineString
  if (
    state.feature.type === 'LineString' &&
    !isCircle(state.feature.properties.curve)
  ) {
    if (e.featureTarget.properties.first) {
      this.changeMode('draw_poly_line', {
        featureId: state.featureId,
        backwards: true,
        curving: true,
      });
    } else if (e.featureTarget.properties.last) {
      this.changeMode('draw_poly_line', {
        featureId: state.featureId,
        curving: true,
      });
    }
  }
};

// when user holds down cmd key, start box selection
CustomDirectSelect.onMouseDown = function (state, e) {
  // Check for Cmd key (Mac) or Ctrl key (Windows/Linux) for box selection
  const isCmdKey = e.originalEvent && (e.originalEvent.metaKey || e.originalEvent.ctrlKey);

  if (this.drawConfig.boxSelect && isCmdKey) {
    e.originalEvent.preventDefault();
    e.originalEvent.stopPropagation();
    // Disable map drag pan to prevent map dragging during box selection
    this.map.dragPan.disable();
    return this.startBoxSelect(state, e);
  }

  // If Cmd is not held, run original DirectSelect.onMouseDown behavior
  return DirectSelect.onMouseDown.call(this, state, e);
};

CustomDirectSelect.onDrag = function (state, e) {
  // Handle box selection
  if (this.drawConfig.boxSelect && state.canBoxSelect) {
    return this.whileBoxSelect(state, e);
  }

  // If not box selecting, run original DirectSelect.onDrag behavior
  return DirectSelect.onDrag.call(this, state, e);
};

CustomDirectSelect.startBoxSelect = function (state, e) {
  this.stopExtendedInteractions(state);
  this.map.dragPan.disable();

  state.boxSelectStartLocation = mouseEventPoint(e.originalEvent, this.map.getContainer());
  state.canBoxSelect = true;

  state.boxSelectElement = null;
};

CustomDirectSelect.whileBoxSelect = function (state, e) {
  state.boxSelecting = true;
  this.updateUIClasses({ mouse: Constants.cursors.ADD });

  // Create the box element when dragging starts
  if (!state.boxSelectElement) {
    state.boxSelectElement = document.createElement('div');
    state.boxSelectElement.id = 'kml-box-select';

    const initialX = state.boxSelectStartLocation.x;
    const initialY = state.boxSelectStartLocation.y;

    Object.assign(state.boxSelectElement.style, {
      position: 'absolute',
      pointerEvents: 'none',
      top: '0',
      left: '0',
      width: '0',
      height: '0',
      transform: `translate(${initialX}px, ${initialY}px)`,
      WebkitTransform: `translate(${initialX}px, ${initialY}px)`,
      opacity: '1',
      visibility: 'visible',
      zIndex: '9999',
      border: '2px solid #01A1DD',
      backgroundColor: 'rgba(1, 161, 221, 0.1)',
      borderRadius: '2px',
      transition: 'none',
    });

    // add the box to the map
    this.map.getContainer().appendChild(state.boxSelectElement);
  }

  if (!state.boxSelectAnimationFrame) {
    state.boxSelectAnimationFrame = requestAnimationFrame(() => {
      const current = mouseEventPoint(e.originalEvent, this.map.getContainer());
      const minX = Math.min(state.boxSelectStartLocation.x, current.x);
      const maxX = Math.max(state.boxSelectStartLocation.x, current.x);
      const minY = Math.min(state.boxSelectStartLocation.y, current.y);
      const maxY = Math.max(state.boxSelectStartLocation.y, current.y);

      if (state.boxSelectElement) {
        const translateValue = `translate(${minX}px, ${minY}px)`;
        const width = `${maxX - minX}px`;
        const height = `${maxY - minY}px`;

        Object.assign(state.boxSelectElement.style, {
          transform: translateValue,
          WebkitTransform: translateValue,
          width,
          height,
        });
      }

      state.boxSelectAnimationFrame = null;
    });
  }
};

CustomDirectSelect.onMouseUp = function (state, e) {
  if (state.dragMoving) {
    this.fireUpdate();
  } else if (state.boxSelecting) {
    const endPoint = mouseEventPoint(e.originalEvent, this.map.getContainer());
    const bbox = [state.boxSelectStartLocation, endPoint];

    // Get all vertices within the box selection area
    const allFeatures = this.featuresAt(null, bbox, 'click');
    const verticesInBox = allFeatures.filter((feature) => (
      feature.properties.meta === Constants.meta.VERTEX &&
      feature.properties.parent === state.featureId
    ));

    if (verticesInBox.length > 0) {
      const selectedCoordPaths = verticesInBox.map((vertex) => vertex.properties.coord_path);

      state.selectedCoordPaths = selectedCoordPaths;
      const selectedCoordinates = this.pathsToCoordinates(state.featureId, state.selectedCoordPaths);
      this.setSelectedCoordinates(selectedCoordinates);
      this.fireActionable(state);
      this.updateUIClasses({ mouse: Constants.cursors.MOVE });
    }
  }

  this.stopExtendedInteractions(state);
};

CustomDirectSelect.stopExtendedInteractions = function (state) {
  if (state.boxSelectAnimationFrame) {
    cancelAnimationFrame(state.boxSelectAnimationFrame);
    state.boxSelectAnimationFrame = null;
  }

  // Remove the box from the map
  const customBox = document.getElementById('kml-box-select');
  if (customBox && customBox.parentNode) {
    customBox.parentNode.removeChild(customBox);
  }

  if (state.boxSelectElement) {
    if (state.boxSelectElement.parentNode) state.boxSelectElement.parentNode.removeChild(state.boxSelectElement);
    state.boxSelectElement = null;
  }

  this.map.dragPan.enable();
  state.boxSelecting = false;
  state.canBoxSelect = false;
  state.dragMoving = false;
  state.canDragMove = false;
};

CustomDirectSelect.startDragging = function (state, e) {
  this.map.dragPan.disable();
  state.canDragMove = true;
  state.dragMoveLocation = e.lngLat;
};

CustomDirectSelect.stopDragging = function (state) {
  this.map.dragPan.enable();
  state.dragMoving = false;
  state.canDragMove = false;
  state.dragMoveLocation = null;
};

CustomDirectSelect.__stopDragging = CustomDirectSelect.stopDragging;
CustomDirectSelect.stopDragging = function (state) {
  if (state.dragMoving && state.feature.properties.curve) {
    state.feature.properties.curve = regularizeMidpoints(
      state.feature.properties.curve,
    );
    state.feature.setCoordinates(curveToCoords(state.feature.properties.curve));
  }
  this.__stopDragging(state);
};

CustomDirectSelect.dragCurveVertex = function (state, e, delta) {
  function coordsToPointFeature(coords) {
    return {
      type: Constants.geojsonTypes.FEATURE,
      properties: {},
      geometry: {
        type: Constants.geojsonTypes.POINT,
        coordinates: coords,
      },
    };
  }

  function getCoordString(curve, elementId) {
    const element = curve[elementId + 1]; // +1 because element type
    return (element || []).slice(1);
  }

  // watch out for all the weird off-by-ones. each layer of a curve structure contains a ['type', ...], although the ids still count from
  // 0: ['compoundcurve', ['linestring', [4, 5], [5, 5]]] has two coords, 0.0 and 0.1
  function getCoordsToMove() {
    // if we do this carefully, we can adjust the array in-place
    const selectedCurveCoords = state.selectedCoordPaths.flatMap(
      (coordPath) => {
        // let [ringId, elementId, coordId] = coordPath.split(".").map(Number);
        const [coordId, elementId, ringId] = coordPath
          .split('.')
          .map(Number)
          .reverse();
        const curve =
          ringId === undefined
            ? state.feature.properties.curve
            : state.feature.properties.curve[ringId + 1];

        const coords = [getCoordString(curve, elementId)[coordId]];
        // if we move the first or last coordinate in a linestring/circularstring, we also need to move the neighbouring last/first
        if (coordId === 0 && elementId > 0) {
          // moving first coord: also move last of previous
          coords.push(...getCoordString(curve, elementId - 1).slice(-1));
        } else if (coordId === getCoordString(curve, elementId).length - 1) {
          // moving last coord: also move first of next
          coords.push(...getCoordString(curve, elementId + 1).slice(0, 1));
        }
        if (ringId !== undefined) {
          if (coordId === 0 && elementId === 0) {
            // moving first coordinate in polygon ring, also move last
            coords.push(...getCoordString(curve, curve.length - 2).slice(-1));
          } else if (
            coordId === curve[elementId + 1].length - 2 &&
            elementId === curve.length - 2
          ) {
            // moving last coordinate in polygon ring, also move first
            coords.push(...getCoordString(curve, 0).slice(0, 1));
          }
        }
        return coords;
      },
    );
    // deduplicate - otherwise risk of moving one point twice
    return [...new Set(selectedCurveCoords)];
  }
  const uniqueCoords = getCoordsToMove();
  const constrainedDelta = constrainFeatureMovement(
    uniqueCoords.map(coordsToPointFeature),
    delta,
  );
  uniqueCoords.forEach((curveCoord) => {
    curveCoord[0] += constrainedDelta.lng;
    curveCoord[1] += constrainedDelta.lat;
  });
  state.feature.setCoordinates(curveToCoords(state.feature.properties.curve));
};

CustomDirectSelect.__dragVertex = DirectSelect.dragVertex;
CustomDirectSelect.dragVertex = function (state, e, delta) {
  if (state.feature.properties.curve) {
    return this.dragCurveVertex(state, e, delta);
  }
  return this.__dragVertex(state, e, delta);
};

CustomDirectSelect.dragFeature = function (state, e, delta) {
  if (state.useMode === 'kmlDraw') {
    this.stopDragging(state);
  } else {
    moveFeatures(this.getSelected(), delta);
    if (state.feature.properties.curve) {
      state.feature.properties.curve = moveCurve(
        state.feature.properties.curve,
        delta,
      );
    }
    state.dragMoveLocation = e.lngLat;
  }
};

// we duplicate this whole function to replace the call to createSupplementaryPoints
CustomDirectSelect.toDisplayFeatures = function (state, geojson, push) {
  if (state.featureId === geojson.properties.id) {
    geojson.properties.active = Constants.activeStates.ACTIVE;
    push(geojson);
    customCreateSupplementaryPoints(geojson, {
      map: this.map,
      midpoints: true,
      useMode: state.useMode,
      selectedPaths: state.selectedCoordPaths,
    }).forEach(push);
  } else {
    geojson.properties.active = Constants.activeStates.INACTIVE;
    push(geojson);
  }
  this.fireActionable(state);
};

// existing AW version
CustomDirectSelect.onTrash = function (state) {
  // Uses number-aware sorting to make sure '9' < '10'. Comparison is reversed because we want them
  // in reverse order so that we can remove by index safely.

  let linestring;

  // if the feature is a polygon and the deletion results in only two vertices
  if (
    state.feature.type === 'Polygon' &&
    state.feature.coordinates.length > 0 &&
    state.feature.coordinates[0].length - state.selectedCoordPaths.length === 2
  ) {
    // prettier-ignore
    const ids = state.selectedCoordPaths.map((coord) => parseInt(coord.split('.')[1], 10));
    linestring = this.newFeature({
      id: state.featureId,
      type: Constants.geojsonTypes.FEATURE,
      properties: state.feature.properties,
      geometry: {
        type: Constants.geojsonTypes.LINE_STRING,
        coordinates: state.feature.coordinates[0].filter(
          (coor, idx) => ids.indexOf(idx) < 0,
        ),
      },
    });
  }

  // this block is from original direct_select onTrash code
  state.selectedCoordPaths
    .sort((a, b) => b.localeCompare(a, 'en', { numeric: true }))
    .forEach((id) => state.feature.removeCoordinate(id));
  this.fireUpdate();
  state.selectedCoordPaths = [];
  this.clearSelectedCoordinates();
  this.fireActionable(state);

  // delete the old polygon
  if (!linestring && state.feature.isValid() === false) {
    this.deleteFeature([state.featureId]);
  }

  // change the polygon to a linestring
  if (linestring) {
    this.map.fire(Constants.events.UPDATE, {
      features: [linestring.toGeoJSON()],
    });
  }

  // switch to simple_select
  this.changeMode(Constants.modes.SIMPLE_SELECT, {});
};

export default CustomDirectSelect;
